import {
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  orderBy,
  limit as fsLimit,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config';
import { type ItemListType } from '../../../../types/common';
import { PatternHistory, CreatePatternHistoryRequest } from '../../../../types/patternHistory';
import { getTotalItemCount } from '../../../../utils/pattern/validation';
import {
  convertItemListToSnakeCase,
  convertItemListToCamelCase,
} from '../../../../utils/pattern/itemListConverter';

/**
 * 直近1件のパターン編集履歴を取得（子パターン判定用）
 */
export const getLatestPatternHistory = async (
  patternId: string,
): Promise<PatternHistory | null> => {
  const historyRef = collection(db, 'patterns', patternId, 'pattern_history');
  const q = query(historyRef, orderBy('version', 'desc'), fsLimit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const docSnap = snapshot.docs[0];
  const data = docSnap.data();
  return {
    history_id: docSnap.id,
    version: (data.version as number) ?? 0,
    prompt: (data.prompt as string) ?? '',
    use_custom_prompt: (data.use_custom_prompt as boolean) ?? false,
    item_list: convertItemListToCamelCase(
      (data.item_list as Parameters<typeof convertItemListToCamelCase>[0]) ?? [],
    ),
    gemini_model: (data.gemini_model as number | null) ?? null,
    changed_by: (data.changed_by as string) ?? '',
    changed_by_name: (data.changed_by_name as string) ?? '',
    changed_at: data.changed_at as Timestamp,
    change_note: (data.change_note as string | undefined) ?? undefined,
  };
};

/**
 * 編集履歴を1件作成。返り値は採番された version と history_id。
 * version は最新履歴 + 1 で採番（履歴が無ければ 1）
 */
export const createPatternHistory = async (
  request: CreatePatternHistoryRequest,
): Promise<{ historyId: string; version: number }> => {
  const latest = await getLatestPatternHistory(request.patternId);
  const nextVersion = (latest?.version ?? 0) + 1;

  const historyRef = collection(db, 'patterns', request.patternId, 'pattern_history');
  const newDocRef = doc(historyRef);
  const data: Record<string, unknown> = {
    history_id: newDocRef.id,
    version: nextVersion,
    prompt: request.prompt ?? '',
    use_custom_prompt: request.useCustomPrompt ?? false,
    item_list: convertItemListToSnakeCase(request.itemList),
    gemini_model: request.geminiModel ?? null,
    ocr_engine: request.ocrEngine ?? null, // #3264
    changed_by: request.changedBy,
    changed_by_name: request.changedByName,
    changed_at: Timestamp.now(),
  };
  if (request.changeNote !== undefined) {
    data.change_note = request.changeNote;
  }

  await setDoc(newDocRef, data);

  return { historyId: newDocRef.id, version: nextVersion };
};

export type PatternHistorySnapshot = {
  prompt: string;
  useCustomPrompt: boolean;
  geminiModel: number | null;
  /** OCRエンジン（#3264）。未設定は null = Vision AI */
  ocrEngine: number | null;
  itemList: ItemListType[];
};

/**
 * PatternHistory を差分比較用のスナップショット形に変換
 */
export const toPatternHistorySnapshot = (history: PatternHistory): PatternHistorySnapshot => ({
  prompt: history.prompt,
  useCustomPrompt: history.use_custom_prompt,
  geminiModel: history.gemini_model,
  ocrEngine: history.ocr_engine ?? null,
  itemList: history.item_list,
});

/**
 * 履歴スナップショットと現在値で子パターンがあるか比較する
 * - prompt
 * - use_custom_prompt
 * - gemini_model
 * - ocr_engine
 * - item_list（snake_case 化したものを JSON 比較）
 */
export const hasPatternHistoryDiff = (
  latest: PatternHistorySnapshot | null,
  current: PatternHistorySnapshot,
): boolean => {
  if (!latest) return true;
  if ((latest.prompt ?? '') !== (current.prompt ?? '')) return true;
  if ((latest.useCustomPrompt ?? false) !== (current.useCustomPrompt ?? false)) return true;
  if ((latest.geminiModel ?? null) !== (current.geminiModel ?? null)) return true;
  if ((latest.ocrEngine ?? null) !== (current.ocrEngine ?? null)) return true;
  const latestItemListSnake = convertItemListToSnakeCase(latest.itemList);
  const currentItemListSnake = convertItemListToSnakeCase(current.itemList);
  return JSON.stringify(latestItemListSnake) !== JSON.stringify(currentItemListSnake);
};

/**
 * Firestore ドキュメントを PatternHistory に変換する共通ヘルパー
 */
const mapDocToPatternHistory = (docId: string, data: Record<string, unknown>): PatternHistory => ({
  history_id: docId,
  version: (data.version as number) ?? 0,
  prompt: (data.prompt as string) ?? '',
  use_custom_prompt: (data.use_custom_prompt as boolean) ?? false,
  item_list: convertItemListToCamelCase(
    (data.item_list as Parameters<typeof convertItemListToCamelCase>[0]) ?? [],
  ),
  gemini_model: (data.gemini_model as number | null) ?? null,
  ocr_engine: (data.ocr_engine as number | null) ?? null,
  changed_by: (data.changed_by as string) ?? '',
  changed_by_name: (data.changed_by_name as string) ?? '',
  changed_at: data.changed_at as Timestamp,
  change_note: (data.change_note as string | undefined) ?? undefined,
});

/**
 * 全パターン編集履歴を新しい順で取得（精度確認画面用）
 */
export const getPatternHistories = async (patternId: string): Promise<PatternHistory[]> => {
  const historyRef = collection(db, 'patterns', patternId, 'pattern_history');
  const q = query(historyRef, orderBy('version', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => mapDocToPatternHistory(d.id, d.data()));
};

/**
 * 過去の履歴バージョンの内容を現在のパターンに適用（ロールバック）。
 * パターン本体の更新と新しい履歴の作成を writeBatch でアトミックに行う。
 */
export const rollbackPatternToHistory = async (
  patternId: string,
  history: PatternHistory,
  userId: string,
  userName: string,
): Promise<{ historyId: string; version: number }> => {
  const patternRef = doc(db, 'patterns', patternId);
  const itemListSnake = convertItemListToSnakeCase(history.item_list);
  const itemCount = getTotalItemCount(history.item_list);

  const latest = await getLatestPatternHistory(patternId);
  const nextVersion = (latest?.version ?? 0) + 1;

  const historyCollection = collection(db, 'patterns', patternId, 'pattern_history');
  const newHistoryRef = doc(historyCollection);

  const batch = writeBatch(db);
  batch.update(patternRef, {
    item_list: itemListSnake,
    item_count: itemCount,
    prompt: history.prompt,
    use_custom_prompt: history.use_custom_prompt,
    gemini_model: history.gemini_model,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  batch.set(newHistoryRef, {
    history_id: newHistoryRef.id,
    version: nextVersion,
    prompt: history.prompt,
    use_custom_prompt: history.use_custom_prompt,
    item_list: itemListSnake,
    gemini_model: history.gemini_model,
    changed_by: userId,
    changed_by_name: userName,
    changed_at: serverTimestamp(),
    change_note: `v${history.version} の内容を適用`,
  });

  await batch.commit();

  return { historyId: newHistoryRef.id, version: nextVersion };
};

/**
 * パターンの全編集履歴から、これまでに使用された項目ID（親・子）を収集する（#3242）
 *
 * 項目を削除すると item_list から消えるため、現在のリストだけでは過去に使ったIDが分からず、
 * 新規項目に削除済みIDが再利用されてしまう。履歴のスナップショットを走査することで、
 * 削除済みのIDも含めた「使用済みID」を復元する。
 *
 * 履歴の item_list は snake_case で保存されているが、ID は変換の影響を受けないため
 * camelCase 変換を挟まずそのまま読む（全バージョンを変換するコストを避ける）。
 *
 * @param patternId - 対象パターンID
 * @returns 使用済み項目IDの集合。履歴が無い場合は空集合
 */
export const getUsedItemIdsFromHistory = async (patternId: string): Promise<Set<string>> => {
  const historyRef = collection(db, 'patterns', patternId, 'pattern_history');
  const snapshot = await getDocs(historyRef);

  const usedIds = new Set<string>();
  snapshot.docs.forEach(d => {
    const itemList = d.data().item_list;
    if (!Array.isArray(itemList)) return;
    itemList.forEach((item: { id?: string; children?: { id?: string }[] }) => {
      if (item?.id && item.id !== '0') usedIds.add(String(item.id));
      if (!Array.isArray(item?.children)) return;
      item.children.forEach(child => {
        if (child?.id && child.id !== '0') usedIds.add(String(child.id));
      });
    });
  });
  return usedIds;
};
